import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OrgContext from '../context/OrgContext';
import AttendanceTable from '../components/AttendanceTable';
import * as XLSX from 'xlsx';

const AttendanceStats = () => {
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [userID, setUserID] = useState('');
    const [locationID, setLocationID] = useState('');
    const [locations, setLocations] = useState([]);
    const [users, setUsers] = useState([]);
    const [workSessions, setWorkSessions] = useState([]);
    const [summaryByUser, setSummaryByUser] = useState([]);

    const token = sessionStorage.getItem('token');
    const { activeOrg } = useContext(OrgContext);

    useEffect(() => {
        if (activeOrg) {
            fetchLocations();
            fetchUsers();
        }
    }, [activeOrg]);

    const fetchLocations = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/locations`, {
                headers: { Authorization: `Bearer ${token}`, orgid: activeOrg._id }
            });
            setLocations(response.data);
        } catch (error) {
            toast.error('Error fetching locations.');
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/users`, {
                headers: { Authorization: `Bearer ${token}`, orgid: activeOrg._id }
            });
            setUsers(response.data);
        } catch (error) {
            toast.error('Error fetching users.');
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/attendance/stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { userID, locationID, startDate: startDate.toISOString(), endDate: endDate.toISOString() },
            });

            setWorkSessions(response.data.workSessions);
            setSummaryByUser(response.data.summaryByUser);
        } catch (error) {
            toast.error('Error fetching statistics.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchStats();
    };

    const exportToExcel = () => {
        const currency = activeOrg?.valute || 'N/A';
        const data = workSessions.map(session => ({
            Date: session.date,
            User: session.user.name,
            IN: `${new Date(session.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${session.locationCheckIn || 'Unknown'})`,
            OUT: `${new Date(session.checkOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${session.locationCheckOut || 'Unknown'})`,
            'Hours Worked': session.hoursWorked,
            'Pay Status': session.paid ? 'Paid' : 'Unpaid',
            Pay: `${session.pay} ${currency}`
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
        XLSX.writeFile(workbook, "Attendance.xlsx");
    };

    return (
        <div className="bg-gray-900 p-6 rounded-lg shadow-lg">
            <form onSubmit={handleSubmit} className="flex flex-wrap md:flex-row gap-4 mb-4 p-4 bg-gray-800 rounded-lg shadow-lg">
                <div className="flex flex-col">
                    <label className="block text-white mb-2">Start Date</label>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy/MM/dd"
                        className="px-4 py-2 bg-gray-700 text-white rounded"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="block text-white mb-2">End Date</label>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy/MM/dd"
                        className="px-4 py-2 bg-gray-700 text-white rounded"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="block text-white mb-2">Location</label>
                    <select
                        value={locationID}
                        onChange={(e) => setLocationID(e.target.value)}
                        className="px-4 py-2 bg-gray-700 text-white rounded"
                    >
                        <option value="">All Locations</option>
                        {locations.map(location => (
                            <option key={location._id} value={location._id}>
                                {location.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col">
                    <label className="block text-white mb-2">User</label>
                    <select
                        value={userID}
                        onChange={(e) => setUserID(e.target.value)}
                        className="px-4 py-2 bg-gray-700 text-white rounded"
                    >
                        <option value="">All Users</option>
                        {users.map(user => (
                            <option key={user._id} value={user._id}>
                                {user.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2 mt-6">
                    <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
                        View
                    </button>
                    <button onClick={exportToExcel} type="button" className="flex items-center bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        <FontAwesomeIcon icon={faFileExcel} className="mr-2" /> Export to Excel
                    </button>
                </div>
            </form>

            {/* Attendance Table with Summary */}
            <AttendanceTable workSessions={workSessions} summaryByUser={summaryByUser} activeOrg={activeOrg} />
        </div>
    );
};

export default AttendanceStats;
